<template>
  <div id="inspectors-list">
    <div id="inspectors-top-panel">
      <div id="title-container">
        <h2>{{ t('inspectors.inspectors') }}</h2>
        <div id="inspectors-counter">{{ inspectors?.length ?? 0 }}</div>
      </div>
      <vf-button
        v-if="isUserRole(addInspectorAllowedRoles)"
        :size="buttonSizes.large"
        :variant="buttonVariants.secondary"
        @click="openAddInspectorModal"
      >
        {{ t('inspectors.addInspector') }}
      </vf-button>
    </div>

    <ad-group-modal
      ref="modalRef"
      :group="adGroup"
      :title="t('inspectors.addInspector')"
      @selected-user="handleAddInspector"
    />

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      action-icon="delete"
      :display-action-icon="isUserRole(addInspectorAllowedRoles)"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="inspectors"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      @action-icon-click="deleteInspectorFromProject"
    ></table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '../../composables/useToast';
  import axios, { isAxiosError } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import { Role, IInspector, AdGroup } from 'types';
  import SpGroupModal from '../SpGroupModal/SpGroupModal.vue';
  import AdGroupModal from '../AdGroupModal/AdGroupModal.vue';
  import TableWrapper from '../TableWrapper/TableWrapper.vue';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';

  const props = defineProps({
    projectId: {
      type: String,
      required: true,
    },
  });

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast, createWarningToast } =
    useToast();

  const inspectors = ref<IInspector[]>([]);
  const adGroup = ref(AdGroup.INSPECTOR);

  const getInspectors = async () => {
    isLoading.value = true;
    try {
      const response = await axios.get(
        endpointsList.projects.getInspectorsForProject(props.projectId),
      );
      inspectors.value = response.data;

      const isUnkownUser = inspectors.value.some(
        (inspector) => inspector.firstName === 'unknown',
      );
      if (isUnkownUser) {
        createWarningToast(t('inspectors.warnings.unknownUser'));
      }
    } catch {
      createErrorToast(t('inspectors.couldNotGetInspectors'));
    }
    isLoading.value = false;
  };

  const { pageOptions, filters, isLoading } = useTableWrapper(
    TableMode.CLIENT_SIDE,
    'lastName',
    getInspectors,
    undefined,
    false,
  );

  const filterConfiguration = new TableFiltersBuilder<IInspector>({
    translationKey: 'inspectors',
  })
    .addInputFilter('firstName', {
      width: '208px',
    })
    .addInputFilter('lastName', {
      width: '208px',
    })
    .addInputFilter('email', {
      width: '208px',
    })
    .build();

  const tableFields = computed(() =>
    new TableFieldsBuilder<IInspector>(
      {
        sortable: true,
        labelTranslationKey: 'generic',
      },
      { actionIcon: true },
    )
      .addField('firstName', { width: '36%' })
      .addField('lastName', { width: '28%' })
      .addField('email', { width: '36%' })
      .build(),
  );

  const handleAddInspector = async (inspector: IInspector) => {
    try {
      const response = await axios.patch(
        endpointsList.projects.addInspectorToProject(
          props.projectId,
          inspector.oid,
        ),
      );
      if (response.status === 200) {
        createSuccessToast(t('inspectors.inspectorAdded'));
        getInspectors();
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        createErrorToast(t('inspectors.inspectorAlreadyAdded'));
      } else {
        createErrorToast(t('inspectors.couldNotAddInspector'));
      }
    }
  };

  const deleteInspectorFromProject = async (inspector: IInspector) => {
    try {
      await axios.delete(
        endpointsList.projects.removeInspectorFromProject(
          props.projectId,
          inspector.oid,
        ),
      );
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        createErrorToast(t('inspectors.errors.cannotRemoveLast'));
      } else {
        createErrorToast(t('inspectors.errors.errorRemovingInspector'));
      }
    }
    await getInspectors();
  };

  const addInspectorAllowedRoles = ref([Role.Admin, Role.DocuManager]);

  const modalRef = ref<InstanceType<typeof SpGroupModal> | null>(null);
  function openAddInspectorModal() {
    modalRef.value?.show();
  }
</script>

<style scoped lang="scss">
  #inspectors-list {
    margin-top: 66px;
    width: 100%;

    #inspectors-top-panel {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 27px;

      #title-container {
        display: flex;
        align-items: center;
        column-gap: 10px;

        h2 {
          font-size: 36px;
          font-weight: 700;
        }

        #inspectors-counter {
          align-self: flex-start;
          background-color: black;
          color: white;
          font-size: 12px;
          border-radius: 50px;
          padding: 3px 20px;
        }
      }
    }

    @include table-action-icon-on-hover;
  }
</style>
